import React, { useState, useEffect } from "react"
import { Modal, Button } from "react-bootstrap"
import { useIntl } from "react-intl"
import PropTypes from "prop-types"
import moment from "moment"

import { LocaleFormattedUser } from "../../internationalization/LocaleFormatted"
import { API_STATUS } from "../../../shared_components/widgets/utils"
import PageLoadingPanel from "../../../shared_components/widgets/PageLoadingPanel"
import PageErrorPanel from "../../../shared_components/widgets/PageErrorPanel"
import SampleStatus from "../resources/SampleStatus"
import { LocaleFormattedDate } from "../../internationalization/LocaleFormatted"
import { withSampleConfig } from "../../../config/SampleConfigProvider"
import { isNumber, parseDate, getCurrentTime } from "../../../shared_components/utils"
import { testItemColumns, getRowSpanNum } from "../SampleScheduleTab/SampleSchedule"
import { MethodColumnSpanner } from "../resources/MethodColumnSpanner"
import { getFormattedMessage } from "../../shared_components_registration"

const processSampleRndData = (sample, setData) => {
  let data = Object.assign({}, sample)

  data.samples.forEach((sample) => {
    sample.has_sampling_time = sample.sampling_time != null
    sample.sampling_time = moment(
      !sample.has_sampling_time ? getCurrentTime() : parseDate(sample.sampling_time),
    ).format("YYYY-MM-DDTHH:mm")
    sample.sample_source_type_id =
      (sample.sample_source_type == null
        ? sample.sampling_point["default_sample_source_type"]
        : (sample.sample_source_type_id = sample.sample_source_type.id)) + ""
  })

  setData(data)
}

const ModalViewSampleRound = ({
  show,
  readonly,
  onHide,
  onSuccess,
  sampleround,
  // SampleConfigProvider
  user_permissions = [],
  isNSView,
  sample_source_types,
  sample_options_status,
  sample_round_stop_status,
  getSampleOptions,
  stopSampleRound,
  updateMetrics,
  // --- SampleConfigProvider
}) => {
  const intl = useIntl()
  const [sampleSourceTypes, setSampleSourceTypes] = useState([])
  const [statusInit, setStatusInit] = useState(API_STATUS.LOADING)
  const [status_submit, setStatusSubmit] = useState(null)
  const [data, setData] = useState(null)

  useEffect(() => {
    setSampleSourceTypes(sample_source_types)
  }, [sample_source_types])

  useEffect(() => {
    setStatusInit(sample_options_status)
  }, [sample_options_status])

  useEffect(() => {
    processSampleRndData(sampleround, setData)
  }, [sampleround])

  useEffect(() => {
    if (sample_round_stop_status === API_STATUS.ERROR) {
      alert(intl.formatMessage({ id: "samplemanagement.unable.to.stop.sample.round" }))
    } else if (sample_round_stop_status === API_STATUS.SUCCESS) {
      updateMetrics({ sample_round_stop_status: API_STATUS.IDLING })
      onSuccess()
    }

    setStatusSubmit(sample_round_stop_status)
  }, [sample_round_stop_status])

  const onInitialLoad = () => {
    getSampleOptions()
    processSampleRndData(sampleround, setData)
  }

  const updateSampleValue = (name, value, idx) => {
    let smp_rnd_data = Object.assign({}, data)
    smp_rnd_data.samples[idx][name] = value
    setData(smp_rnd_data)
  }

  const doStopRound = (event) => {
    event.preventDefault()

    let smp_rnd_data = Object.assign({}, data)
    smp_rnd_data.samples.forEach((sample) => {
      sample.sampling_time = moment(sample.sampling_time).format("YYYY-MM-DDTHH:mm:ssZZ") // For sending to the backend with tz offset
    })

    stopSampleRound(smp_rnd_data.id, smp_rnd_data)
  }

  const headers = [
    {
      id: "samplemanagement.sample.id",
      desc: "Sample ID",
      style: { minWidth: "100px" },
    },
    {
      id: "samplemanagement.sample.connection.number",
      desc: "Sample Connection Number",
    },
    {
      id: "samplemanagement.stream.name.and.pfd.stream.number",
      desc: "Stream Name and PFD Stream Number",
      style: { minWidth: "100px" },
    },
    {
      id: "general.state",
      desc: "Gas, Liquid, or Solid",
      style: { minWidth: "60px" },
    },
    { id: "general.reactor", desc: "Reactor(s)" },
    { id: "general.tag", desc: "Tag Name", style: { minWidth: "130px" } },
    {
      id: "samplemanagement.test.item",
      desc: "Test Item",
      style: { minWidth: "130px" },
    },
    { id: "general.units", desc: "Units" },
    {
      id: "samplemanagement.sample.source.type",
      desc: "Sample Source Type",
      style: { minWidth: "100px" },
    },
    { id: "general.time", desc: "Time", style: { minWidth: "100px" } },
    { id: "general.status", desc: "Status", style: { minWidth: "100px" } },
  ]

  const hideModal = () => {
    updateMetrics({ sample_round_stop_status: API_STATUS.IDLING })
    onHide()
  }

  return (
    <Modal
      show={show}
      onHide={hideModal}
      backdrop={"static"}
      style={{ overflowX: "scroll", overflowY: "scroll" }}
      keyboard={true}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <i className={"fa fa-retweet"} />
          &nbsp;&nbsp;
          {getFormattedMessage(
            `samplemanagement.tab.${isNSView ? "ns" : "sample"}.round`,
            `${isNSView ? "NS" : "Sample"} Round`,
          )}
          &nbsp;&nbsp;
          {data ? "#" + data.id : ""}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div>
          {statusInit === API_STATUS.LOADING && <PageLoadingPanel />}

          {statusInit === API_STATUS.ERROR && <PageErrorPanel attemptReload={onInitialLoad} />}

          {statusInit === API_STATUS.SUCCESS && (
            <>
              <table className={"tbl_details_sampleround"}>
                <tbody>
                  <tr>
                    <td style={{ width: "150px" }}>
                      <label>{getFormattedMessage("general.id", "ID")}:</label>
                    </td>
                    <td style={{ width: "300px" }}>{data.id}</td>
                    <td style={{ width: "150px" }}>
                      <label>{getFormattedMessage("general.description", "Description")}:</label>
                    </td>
                    <td style={{ width: "300px" }}>{data.description || "-"}</td>
                  </tr>
                  <tr>
                    <td>
                      <label>{getFormattedMessage("general.status", "Status")}:</label>
                    </td>
                    <td>
                      {data["is_active"] ? (
                        <span className={"text-success"}>
                          <span className={"fa fa-play text-success"} />
                          &nbsp;
                          {getFormattedMessage("general.running", "Running")}
                        </span>
                      ) : (
                        <>{getFormattedMessage("general.complete", "Complete")}</>
                      )}
                    </td>
                    <td>
                      <label>{getFormattedMessage("general.operator", "Operator")}:</label>
                    </td>
                    <td>
                      <LocaleFormattedUser user={data["created_by"]} />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <label>{getFormattedMessage("samplemanagement.start.time", "Start Time")}:</label>
                    </td>
                    <td>
                      <LocaleFormattedDate date={data?.start_time} shouldParse={true} />
                    </td>
                    <td>
                      <label>{getFormattedMessage("samplemanagement.end.time", "End Time")}:</label>
                    </td>
                    <td>
                      {data?.end_time ? (
                        <LocaleFormattedDate date={data?.end_time} shouldParse={true} />
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <label>{getFormattedMessage("general.duration", "Duration")}:</label>
                    </td>
                    <td>{isNumber(data["duration_min"]) ? data["duration_min"].toFixed(2) + " min" : "-"}</td>
                  </tr>
                </tbody>
              </table>
              <form name={"form_sampleround"} onSubmit={doStopRound} style={{ marginTop: "20px" }}>
                <h5>{getFormattedMessage("samplemanagement.sampling.points", "Sampling Points")}</h5>
                <hr style={{ margin: "5px 0px 10px 0px" }} />
                <table className={"table table-bordered table-sm tbl_lanzatable"}>
                  <thead>
                    <tr>
                      {headers.map((header) => (
                        <th
                          key={header["id"]}
                          className={"text-center"}
                          style={header["style"] ? header["style"] : {}}
                        >
                          {getFormattedMessage(header["id"], header["desc"])}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.samples.map((row, smp_i) => {
                      const filteredMethods = {}
                      let firstMethod = null
                      Object.entries(row?.sampling_point?.methods || {}).forEach(([method_name, info]) => {
                        if (row.analyzable_methods.includes(method_name)) {
                          Object.keys(filteredMethods).length === 0 ? (firstMethod = method_name) : null
                          filteredMethods[method_name] = info
                        }
                      })
                      const row_span = getRowSpanNum({ ...row.sampling_point, methods: filteredMethods })
                      const methodSpannerParams = (name) => ({
                        name: name,
                        colSpan: 3,
                        method_info: row?.sampling_point?.methods?.[name],
                        onMethodClick: () => {},
                      })
                      return (
                        <React.Fragment key={smp_i}>
                          <tr>
                            <td className={"text-success text-center"} rowSpan={row_span}>
                              {row.sample_id}
                              {row.alias && (
                                <>
                                  <br />
                                  <span
                                    className={"text-success text-center"}
                                    style={{ fontSize: "12px" }}
                                  >{`(${row.alias})`}</span>
                                </>
                              )}
                            </td>
                            {[
                              row.sampling_point["sample_conn_no"],
                              `${row.sampling_point["stream_name"] || ""} (${
                                row.sampling_point["pfd_stream_no"] || "-"
                              })`,
                              row.sampling_point["sample_state"],
                              row.sampling_point["reactor_list"].map((r) => r.name).join(", "),
                            ].map((val, valIDX) => (
                              <td rowSpan={row_span} className={"text-center"} key={`r${smp_i}c${valIDX}`}>
                                {val}
                              </td>
                            ))}
                            {firstMethod ? (
                              <MethodColumnSpanner {...methodSpannerParams(firstMethod)} />
                            ) : (
                              testItemColumns(isNSView, null, "ti_1", false, false)
                            )}
                            <td rowSpan={row_span} className={"text-center"}>
                              {readonly || row.sample_source_type ? (
                                row.sample_source_type ? (
                                  row.sample_source_type.name
                                ) : (
                                  "-"
                                )
                              ) : (
                                <select
                                  className={"form-select form-select-sm"}
                                  name={"sample_source_type_id"}
                                  value={row.sample_source_type_id || ""}
                                  onChange={(evt) =>
                                    updateSampleValue(evt.target.name, evt.target.value, smp_i)
                                  }
                                  required
                                >
                                  <option value={""}>
                                    {intl.formatMessage({
                                      id: "general.dropdown.please.select",
                                    })}
                                  </option>
                                  {sampleSourceTypes.map((ch, k) => (
                                    <option key={k} value={ch.value}>
                                      {ch.display_name}
                                    </option>
                                  ))}
                                </select>
                              )}
                            </td>
                            <td rowSpan={row_span} className={"text-center"}>
                              {readonly || row.has_sampling_time ? (
                                <LocaleFormattedDate date={row.sampling_time} shouldParse={true} />
                              ) : (
                                <input
                                  type={"datetime-local"}
                                  className={"form-control form-control-sm"}
                                  name={"sampling_time"}
                                  value={row.sampling_time || ""}
                                  required
                                  onChange={(evt) =>
                                    updateSampleValue(evt.target.name, evt.target.value, smp_i)
                                  }
                                />
                              )}
                            </td>
                            <td className={"text-success text-center"} rowSpan={row_span}>
                              <SampleStatus status={row.status} />
                            </td>
                          </tr>
                          {Object.values(filteredMethods || {}).map((method, m_idx) => (
                            <React.Fragment key={`m${m_idx}`}>
                              {m_idx > 0 && (
                                <tr key={`m${m_idx}`}>
                                  <MethodColumnSpanner {...methodSpannerParams(method.name)} />
                                </tr>
                              )}
                              {method?.open &&
                                method?.test_items.map((ti, ti_idx) => (
                                  <tr key={`s${smp_i}_m${m_idx}_ti${ti_idx}`}>
                                    {testItemColumns(isNSView, ti, `ti${ti_idx}`, false, false)}
                                  </tr>
                                ))}
                            </React.Fragment>
                          ))}
                        </React.Fragment>
                      )
                    })}
                  </tbody>
                </table>
                {status_submit === API_STATUS.ERROR && (
                  <p
                    style={{ marginTop: "10px", marginBottom: "20px" }}
                    className={"text-center error-message"}
                  >
                    {getFormattedMessage("general.server.error", "Error! Could not process request")}
                  </p>
                )}
                {!readonly && (
                  <div style={{ marginTop: "10px" }} align={"center"}>
                    <Button
                      variant={"danger"}
                      type={"submit"}
                      disabled={
                        status_submit === API_STATUS.LOADING ||
                        !user_permissions.includes("change_sampleround")
                      }
                    >
                      <span className={"fa fa-stop text_white"} />
                      &nbsp;
                      {getFormattedMessage("samplemanagement.complete.round", "Complete Round")}
                    </Button>
                  </div>
                )}
              </form>
            </>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant={"outline-primary"} onClick={hideModal} style={{ marginRight: "auto" }}>
          {getFormattedMessage("general.closeButton", "Close")}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default withSampleConfig(ModalViewSampleRound)

ModalViewSampleRound.propTypes = {
  show: PropTypes.bool.isRequired,
  readonly: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  sampleround: PropTypes.object.isRequired,
  user_permissions: PropTypes.array,
  isNSView: PropTypes.bool.isRequired,
  sample_source_types: PropTypes.array,
  sample_options_status: PropTypes.string,
  sample_round_stop_status: PropTypes.string,
  getSampleOptions: PropTypes.func.isRequired,
  stopSampleRound: PropTypes.func.isRequired,
  updateMetrics: PropTypes.func.isRequired,
}
